import * as React from 'react';

import Select, { createFilter } from 'react-select';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import _ from 'lodash';

import classes from './StringAssignField.module.scss';

const defaultTextFilter = createFilter({ ignoreAccents: false });

const normalizeStringNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const StringAssignField = ({
  editingActivity,
  strings,
  label,
  value,
  localeOverride,
  withoutAudio,
  changeAudioHandler,
  changeTextHandler,
  changeAllHandler,
  labelClassName,
}) => {
  const [inputValue, setInputValue] = React.useState('');

  const locale = React.useMemo(
    () => localeOverride || editingActivity?.activity?.locale || 'en',
    [localeOverride, editingActivity?.activity?.locale]
  );

  const memoizedStrings = React.useMemo(
    () =>
      _.chain(strings || [])
        .orderBy(({ stringNumber }) => Number(stringNumber), ['asc'])
        .map(({ stringNumber, data, _id }) => {
          const numericStringNumber = normalizeStringNumber(stringNumber);
          const stringValue = data?.[locale]?.stringValue || '';

          return {
            label: `${stringNumber}${stringValue ? ` - ${stringValue}` : ''}`,
            value: {
              stringNumber: numericStringNumber,
              _id,
            },
          };
        })
        .filter((option) => option.value.stringNumber)
        .value(),
    [strings, locale]
  );

  const onChange = React.useCallback(
    (option) => {
      const stringNumber = normalizeStringNumber(option?.value?.stringNumber);
      const audio = !withoutAudio && stringNumber ? 'INHERITED' : '';

      changeTextHandler?.(stringNumber);
      changeAudioHandler?.(audio);
      changeAllHandler?.({
        text: stringNumber,
        audio,
      });

      setInputValue('');
    },
    [changeAllHandler, changeAudioHandler, changeTextHandler, withoutAudio]
  );

  const selectValue = React.useMemo(() => {
    const currentStringNumber = normalizeStringNumber(value);

    if (!currentStringNumber) return null;

    return (
      memoizedStrings.find(
        (option) => option.value.stringNumber === currentStringNumber
      ) || null
    );
  }, [memoizedStrings, value]);

  const filterOption = React.useCallback((candidate, rawInput) => {
    const query = String(rawInput || '').trim();

    if (!query) return true;

    if (/^\d+$/.test(query)) {
      return String(candidate.data.value.stringNumber).startsWith(query);
    }

    return defaultTextFilter(candidate, query);
  }, []);

  const onInputChange = React.useCallback((nextValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(nextValue);
    }

    if (actionMeta.action === 'menu-close') {
      setInputValue('');
    }

    return nextValue;
  }, []);

  const onKeyDown = React.useCallback(
    (event) => {
      if (event.key !== 'Enter') return;

      const query = inputValue.trim();
      if (!/^\d+$/.test(query)) return;

      const exactOption = memoizedStrings.find(
        (option) => String(option.value.stringNumber) === query
      );

      if (!exactOption) return;

      event.preventDefault();
      event.stopPropagation();
      onChange(exactOption);
    },
    [inputValue, memoizedStrings, onChange]
  );

  return (
    <>
      {label && <label className={labelClassName}>{label}</label>}

      <Select
        isSearchable
        isClearable
        blurInputOnSelect={false}
        className={classes.stringAssignField}
        filterOption={filterOption}
        value={selectValue}
        inputValue={inputValue}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        onChange={onChange}
        options={memoizedStrings}
        textFieldProps={{
          label: 'Label',
          InputLabelProps: {
            shrink: true,
          },
        }}
      />
    </>
  );
};

StringAssignField.propTypes = {
  strings: PropTypes.array.isRequired,
  editingActivity: PropTypes.object,
  label: PropTypes.string,
  value: PropTypes.any,
  localeOverride: PropTypes.string,
  withoutAudio: PropTypes.bool,
  changeAudioHandler: PropTypes.func,
  changeTextHandler: PropTypes.func,
  changeAllHandler: PropTypes.func,
  labelClassName: PropTypes.string,
};

const mapStateToProps = ({ common, flinkMake }) => ({
  strings: common.strings,
  editingActivity: flinkMake.activity.editingActivity,
});

export default compose(connect(mapStateToProps))(StringAssignField);
